import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import NewsCard from '@/components/feature/NewsCard';
import { comics } from '@/mocks/comics';
import { news } from '@/mocks/news';

type ResultItem =
  | { kind: 'comic'; id: string; score: number; date: string }
  | { kind: 'news'; id: string; score: number; date: string };

const TYPES = [
  { key: 'all', label: '全部' },
  { key: 'comics', label: '漫画作品' },
  { key: 'news', label: '动漫资讯' },
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlQuery = searchParams.get('q') || '';
  const type = searchParams.get('type') || 'all';
  const order = searchParams.get('order') || 'relevance';
  const [keyword, setKeyword] = useState(urlQuery);

  useEffect(() => {
    setKeyword(urlQuery);
  }, [urlQuery]);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (keyword.trim()) next.set('q', keyword.trim());
    else next.delete('q');
    setSearchParams(next);
  };

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  const results = useMemo(() => {
    const q = urlQuery.trim().toLowerCase();
    if (!q) return [] as ResultItem[];
    const items: ResultItem[] = [];

    if (type === 'all' || type === 'comics') {
      comics.forEach((comic) => {
        let score = 0;
        if (comic.title.toLowerCase().includes(q)) score += 10;
        if (comic.genre.toLowerCase().includes(q)) score += 6;
        if (comic.author.toLowerCase().includes(q)) score += 5;
        if (comic.tags.some((tag) => tag.toLowerCase().includes(q))) score += 4;
        if (comic.summary.toLowerCase().includes(q)) score += 2;
        if (score > 0) items.push({ kind: 'comic', id: comic.id, score, date: comic.updatedAt });
      });
    }

    if (type === 'all' || type === 'news') {
      news.forEach((item) => {
        let score = 0;
        if (item.title.toLowerCase().includes(q)) score += 10;
        if (item.category.toLowerCase().includes(q)) score += 6;
        if (item.tags.some((tag) => tag.toLowerCase().includes(q))) score += 5;
        if (item.summary.toLowerCase().includes(q)) score += 3;
        if (item.content.some((p) => p.toLowerCase().includes(q))) score += 1;
        if (score > 0) items.push({ kind: 'news', id: item.id, score, date: item.date });
      });
    }

    items.sort((a, b) => {
      if (order === 'latest') return a.date < b.date ? 1 : -1;
      return b.score - a.score;
    });
    return items;
  }, [urlQuery, type, order]);

  const comicResults = results.filter((r) => r.kind === 'comic');
  const newsResults = results.filter((r) => r.kind === 'news');

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <title>{urlQuery ? `“${urlQuery}”搜索结果 - 漫蛙漫画动漫频道` : '搜索 - 漫蛙漫画动漫频道'}</title>
      <meta name="description" content="在漫蛙搜索漫画作品与动漫资讯，支持关键词模糊匹配，快速找到你感兴趣的内容。" />
      <meta name="keywords" content="漫蛙搜索,漫画搜索,动漫资讯搜索" />

      <SiteHeader />

      <main className="flex-1">
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1440px] px-4 py-8 md:px-8 md:py-10">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '搜索' }]} />
            <h1 className="mt-3 font-heading text-2xl font-800 text-foreground-950 md:text-3xl">站内搜索</h1>
            <form onSubmit={submit} className="mt-5 flex max-w-2xl flex-col gap-3 sm:flex-row">
              <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-3 transition-colors focus-within:border-primary-400">
                <i className="ri-search-line text-base text-foreground-400" />
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="输入漫画名、作者、类型或资讯关键词"
                  className="w-full bg-transparent text-sm font-label text-foreground-800 outline-none placeholder:text-foreground-400"
                />
              </div>
              <button
                type="submit"
                className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-7 py-3 text-sm font-label font-500 text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索
              </button>
            </form>
            {urlQuery && (
              <p className="mt-3 text-sm font-label text-foreground-500">
                当前搜索词：<strong className="font-700 text-primary-700">“{urlQuery}”</strong>
              </p>
            )}
          </div>
        </div>

        <div className="mx-auto w-full max-w-[1440px] px-4 py-8 md:px-8 md:py-10">
          {!urlQuery ? (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
              <span className="mw-float mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-3xl text-primary-600">
                <i className="ri-search-line" />
              </span>
              <h3 className="mt-4 font-heading text-lg font-700 text-foreground-950">输入关键词开始搜索</h3>
              <p className="mt-2 text-sm font-label text-foreground-500">试试搜索“科幻”、“日常”或作品名</p>
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                {['科幻', '日常', '热血', '星海拾荒者', '新番'].map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="cursor-pointer rounded-md bg-background-100 px-3 py-1.5 text-xs font-label text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-800"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="inline-flex items-center gap-1 rounded-full border border-background-300 bg-background-100 p-1">
                    {TYPES.map((item) => (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => setParam('type', item.key === 'all' ? '' : item.key)}
                        className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-label font-500 transition-colors ${
                          type === item.key ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:text-foreground-950'
                        }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm font-label text-foreground-500">
                    找到 <strong className="font-700 text-primary-700">{results.length}</strong> 条结果
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setParam('order', '')}
                      className={`cursor-pointer whitespace-nowrap rounded-md px-3.5 py-2 text-sm font-label transition-colors ${
                        order === 'relevance' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600'
                      }`}
                    >
                      相关性
                    </button>
                    <button
                      type="button"
                      onClick={() => setParam('order', 'latest')}
                      className={`cursor-pointer whitespace-nowrap rounded-md px-3.5 py-2 text-sm font-label transition-colors ${
                        order === 'latest' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600'
                      }`}
                    >
                      更新时间
                    </button>
                  </div>
                </div>
              </div>

              {results.length === 0 ? (
                <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
                  <span className="mw-float mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-3xl text-primary-600">
                    <i className="ri-search-eye-line" />
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-700 text-foreground-950">没有找到相关结果</h3>
                  <p className="mt-2 text-sm font-label text-foreground-500">换个关键词试试，或看看下面这些热门推荐</p>
                  <div className="mt-6 flex flex-wrap justify-center gap-2">
                    {['热血', '科幻', '恋爱', '日常', '奇幻'].map((tag) => (
                      <Link
                        key={tag}
                        to={`/search?q=${encodeURIComponent(tag)}`}
                        className="cursor-pointer rounded-md bg-background-100 px-3 py-1.5 text-xs font-label text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-800"
                      >
                        {tag}
                      </Link>
                    ))}
                  </div>
                  <div className="mx-auto mt-8 grid max-w-4xl grid-cols-2 gap-4 px-4 sm:grid-cols-4">
                    {[...comics].sort((a, b) => b.popularity - a.popularity).slice(0, 4).map((comic) => (
                      <ComicCard key={comic.id} comic={comic} />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-10">
                  {comicResults.length > 0 && (
                    <section>
                      <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-800 text-foreground-950">
                        <i className="ri-book-open-line text-primary-600" />
                        漫画作品（{comicResults.length}）
                      </h2>
                      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5 lg:gap-5">
                        {comicResults.map((result) => {
                          const comic = comics.find((c) => c.id === result.id)!;
                          return <ComicCard key={result.id} comic={comic} />;
                        })}
                      </div>
                    </section>
                  )}

                  {newsResults.length > 0 && (
                    <section>
                      <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-800 text-foreground-950">
                        <i className="ri-newspaper-line text-primary-600" />
                        动漫资讯（{newsResults.length}）
                      </h2>
                      <div className="flex flex-col gap-4">
                        {newsResults.map((result) => {
                          const item = news.find((n) => n.id === result.id)!;
                          return <NewsCard key={result.id} item={item} />;
                        })}
                      </div>
                    </section>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}